import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, Package, Users, DollarSign, 
  TrendingUp, Clock, AlertCircle, CheckCircle
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  processing: 'bg-purple-100 text-purple-800 border-purple-300',
  shipped: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  delivered: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  processing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl text-brand-black">Dashboard</h1>
          <p className="text-brand-dark/60 font-body">Bienvenue dans votre espace administration</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Revenue - Paid Orders Only */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-brand-black border-brand-gold/30">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-gold font-body">Ventes Encaissées</CardTitle>
                <CheckCircle className="h-5 w-5 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-white font-heading">${data?.total_revenue?.toFixed(2) || 0} CAD</div>
                <p className="text-xs text-brand-gold/70 flex items-center gap-1 mt-1 font-body">
                  <TrendingUp className="h-3 w-3" /> Commandes payées
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pending Revenue */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-brand-gold/10 border-brand-gold/30">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-gold font-body">En Attente</CardTitle>
                <Clock className="h-5 w-5 text-brand-gold" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-black font-heading">${data?.pending_revenue?.toFixed(2) || 0} CAD</div>
                <p className="text-xs text-brand-dark/60 flex items-center gap-1 mt-1 font-body">
                  <AlertCircle className="h-3 w-3" /> {data?.pending_orders || 0} à encaisser
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Total Orders */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-dark/70 font-body">Commandes</CardTitle>
                <ShoppingCart className="h-5 w-5 text-brand-gold" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-black font-heading">{data?.total_orders || 0}</div>
                <p className="text-xs text-brand-dark/50 font-body">Total des commandes</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Products */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-dark/70 font-body">Produits</CardTitle>
                <Package className="h-5 w-5 text-brand-gold" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-black font-heading">{data?.total_products || 0}</div>
                <p className="text-xs text-brand-dark/50 font-body">En catalogue</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Second Row - Customers & Total */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-dark/70 font-body">Clients</CardTitle>
                <Users className="h-5 w-5 text-brand-gold" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-black font-heading">{data?.total_customers || 0}</div>
                <p className="text-xs text-brand-dark/50 font-body">Clients inscrits</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="bg-brand-gold border-transparent">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-brand-black font-body">Total Global</CardTitle>
                <DollarSign className="h-5 w-5 text-brand-black" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-brand-black font-heading">
                  ${((data?.total_revenue || 0) + (data?.pending_revenue || 0)).toFixed(2)} CAD
                </div>
                <p className="text-xs text-brand-black/70 font-body">Payé + En attente</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="text-brand-black font-heading">Commandes récentes</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {data?.recent_orders?.length > 0 ? (
                <div className="space-y-3">
                  {data.recent_orders.map(order => (
                    <div key={order.order_id} className="flex items-center justify-between p-4 bg-brand-cream rounded-sm border border-brand-gold/10">
                      <div>
                        <p className="text-brand-black font-medium font-body">#{order.order_id.slice(-8).toUpperCase()}</p>
                        <p className="text-brand-dark/50 text-sm font-body">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusColors[order.order_status]} border`}>
                          {statusLabels[order.order_status]}
                        </Badge>
                        {order.payment_status === 'paid' ? (
                          <Badge className="bg-green-100 text-green-800 border border-green-300">Payé</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300">Non payé</Badge>
                        )}
                      </div>
                      <p className="text-brand-gold font-semibold font-heading">${order.total.toFixed(2)} CAD</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-brand-dark/50 text-center py-8 font-body">Aucune commande récente</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AdminLayout>
  );
}
